import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import StarIcon from "@mui/icons-material/Star";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5003";

// Supported countries for dynamic currency & tax handling
const SUPPORTED_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "United Arab Emirates",
  "Canada",
  "Australia",
  "Singapore",
  "Germany",
  "France",
  "Other"
];

// All 36 Indian States & Union Territories for accurate GST calculation
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

// Helper to dynamically inject Razorpay script if not already present
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const UpgradePlanModal = ({
  open,
  isOpen,
  onClose,
  onUpgradeSuccess,
  tenantId: propTenantId,
  plans: propPlans,
  currentPlan,
  lockedFeature
}) => {
  const isModalOpen = Boolean(open || isOpen);

  // Modal checkout step: 'SELECT_PLAN' | 'ORDER_SUMMARY' | 'PROCESSING' | 'SUCCESS'
  const [step, setStep] = useState("SELECT_PLAN");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCountry, setBillingCountry] = useState("India");
  const [billingState, setBillingState] = useState("Karnataka");
  const [postalCode, setPostalCode] = useState("");
  const [taxId, setTaxId] = useState("");
  const [currencyCode, setCurrencyCode] = useState("INR");
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [error, setError] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [receiptData, setReceiptData] = useState(null);

  const [subscriptionInfo, setSubscriptionInfo] = useState({
    daysRemaining: null,
    seatsUsed: 0,
    maxSeats: null,
    status: "Active",
    isTrial: true
  });

  useEffect(() => {
    if (isModalOpen) {
      setStep("SELECT_PLAN");
      setSelectedPlan(null);
      setError("");
      setOrderData(null);
      setReceiptData(null);

      if (Array.isArray(propPlans) && propPlans.length > 0) {
        setPlans(propPlans);
      } else {
        fetchSubscriptionAndPlans();
      }
    }
  }, [isModalOpen, propPlans]);

  const fetchSubscriptionAndPlans = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      let detectedCurrency = "INR";

      // 1. Fetch Subscription status & Company Billing Profile from API
      try {
        const subRes = await axios.get(`${API_URL}/api/subscription/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 8000
        });

        if (subRes.data) {
          const sub = subRes.data.subscription || {};
          const license = subRes.data.license || {};
          const isGrace = Boolean(subRes.data.is_grace ?? sub.is_grace ?? (sub.status === "GRACE"));
          const graceDays = subRes.data.grace_days_remaining ?? sub.grace_days_remaining ?? null;
          const bp = subRes.data.billing_profile || {};

          const profileCountry = bp.country || "India";
          const isIndia = profileCountry.toLowerCase() === "india";
          const profileState = bp.state || (isIndia ? "Karnataka" : "");
          const profileCurrency = bp.currency_code || (isIndia ? "INR" : "USD");
          const profileSymbol = bp.currency_symbol || (profileCurrency === "USD" ? "$" : "₹");

          setBillingCountry(profileCountry);
          setBillingState(profileState);
          setCurrencyCode(profileCurrency);
          setCurrencySymbol(profileSymbol);
          detectedCurrency = profileCurrency;
          if (bp.tax_id) setTaxId(bp.tax_id);
          
          let storageFormatted = null;
          try {
            const storageRes = await axios.get(`${API_URL}/api/subscription/storage`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              timeout: 6000
            });
            if (storageRes.data?.storage) {
              const st = storageRes.data.storage;
              const used = Number(st.used_storage_bytes) || 0;
              const max = Number(st.max_storage_bytes) || 5368709120;
              const toGB = (b) => (b / (1024 * 1024 * 1024)).toFixed(1);
              storageFormatted = `${toGB(used)} / ${toGB(max)} GB`;
            }
          } catch (stErr) {}

          setSubscriptionInfo({
            daysRemaining: subRes.data.days_remaining ?? sub.days_remaining ?? null,
            seatsUsed: license.used_seats ?? subRes.data.used_seats ?? 0,
            maxSeats: license.max_users ?? sub.max_users ?? subRes.data.max_users ?? null,
            status: isGrace ? "Grace Period" : (sub.status === "EXPIRED" ? "Expired" : (sub.status || "Active")),
            isTrial: Boolean((sub.status === "TRIAL" || sub.is_trial || subRes.data.days_remaining) && !isGrace),
            isGrace: isGrace,
            graceDaysRemaining: graceDays,
            storageUsedFormatted: storageFormatted
          });
        }
      } catch (err) {
        console.warn("Could not fetch subscription status:", err.message);
      }

      // 2. Fetch Pricing Plans dynamically from BCC API filtered by tenant's currency
      const tenantId = getResolvedTenantId();
      const plansRes = await axios.get(`${API_URL}/api/subscription/plans`, {
        params: {
          ...(tenantId ? { tenant_id: tenantId } : {}),
          currency: detectedCurrency
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 10000
      });

      const bccPlans = plansRes.data?.data || plansRes.data?.plans;
      if (Array.isArray(bccPlans) && bccPlans.length > 0) {
        setPlans(bccPlans);
        if (bccPlans[0]?.currency_symbol) {
          setCurrencySymbol(bccPlans[0].currency_symbol);
        }
        if (bccPlans[0]?.currency) {
          setCurrencyCode(bccPlans[0].currency);
        }
      } else {
        setPlans([]);
        setError("No subscription pricing plans found from BCC billing service.");
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Unable to fetch pricing plans from BCC service. Please verify connection.";
      setError(errMsg);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const getResolvedTenantId = () => {
    if (propTenantId) return propTenantId;
    const storedTenant = localStorage.getItem("tenant_id");
    if (storedTenant) return storedTenant;

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        if (parsed.tenant_id) return parsed.tenant_id;
      } catch (e) {}
    }

    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.tenant_id) return payload.tenant_id;
      } catch (e) {}
    }

    return null;
  };

  // Helper to request calculated order & dynamic tax breakdown from BCC
  const createOrderWithBcc = async (plan, countryToUse, stateToUse, taxIdToUse, postalToUse, currToUse) => {
    setOrderLoading(true);
    setError("");

    const targetPlan = plan || selectedPlan;
    if (!targetPlan) {
      setOrderLoading(false);
      return;
    }

    const tenantId = getResolvedTenantId();
    const token = localStorage.getItem("token");

    const targetCountry = countryToUse !== undefined ? countryToUse : billingCountry;
    const targetState = stateToUse !== undefined ? stateToUse : billingState;
    const targetTaxId = taxIdToUse !== undefined ? taxIdToUse : taxId;
    const targetPostal = postalToUse !== undefined ? postalToUse : postalCode;
    const targetCurrency = currToUse || targetPlan.currency || currencyCode || (targetCountry.toLowerCase() === "india" ? "INR" : "USD");

    try {
      const res = await axios.post(
        `${API_URL}/api/subscription/create-order`,
        {
          price_id: targetPlan.price_id || targetPlan.id,
          billing_country: targetCountry,
          billing_state: targetState,
          postal_code: targetPostal,
          tax_id: targetTaxId,
          currency: targetCurrency,
          tenant_id: tenantId
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 12000
        }
      );

      if (res.data?.success && res.data?.data) {
        setOrderData(res.data.data);
        if (res.data.data.currency_symbol) setCurrencySymbol(res.data.data.currency_symbol);
        if (res.data.data.currency_code) setCurrencyCode(res.data.data.currency_code);
        setStep("ORDER_SUMMARY");
      } else {
        setError(res.data?.message || "Failed to create subscription order.");
      }
    } catch (err) {
      console.warn("Using calculated fallback breakdown:", err.message);
      // Fallback calculation in case endpoint is pending connection
      const base = parseFloat(targetPlan.final_amount ?? targetPlan.amount ?? targetPlan.price ?? 0);
      const isIndia = targetCountry.toLowerCase() === "india";
      const isIntra = isIndia && targetState.toLowerCase() === "karnataka";
      const discount = parseFloat(targetPlan.savings_amount || 0);

      let taxTotal = 0;
      let taxLines = [];
      let taxType = "EXPORT";

      if (isIndia) {
        taxTotal = parseFloat((base * 0.18).toFixed(2));
        if (isIntra) {
          taxType = "CGST_SGST";
          const half = parseFloat((taxTotal / 2).toFixed(2));
          taxLines = [
            { label: "CGST", rate: 9, amount: half },
            { label: "SGST", rate: 9, amount: half }
          ];
        } else {
          taxType = "IGST";
          taxLines = [
            { label: "IGST", rate: 18, amount: taxTotal }
          ];
        }
      } else {
        // Export of service under LUT (0% IGST)
        taxTotal = 0;
        taxLines = [
          { label: "Export of Services (LUT - 0% IGST)", rate: 0, amount: 0 }
        ];
      }

      const activeSymbol = targetPlan.currency_symbol || (targetCurrency === "USD" ? "$" : "₹");

      setOrderData({
        breakdown: {
          plan_name: targetPlan.plan_name || targetPlan.name,
          billing_frequency: targetPlan.billing_frequency || "ANNUAL",
          max_users: targetPlan.max_users || 20,
          base_price: parseFloat(targetPlan.amount || base),
          discount_amount: discount,
          subtotal: base,
          tax_lines: taxLines,
          tax_total: taxTotal,
          tax_type: taxType,
          total_amount: base + taxTotal,
          billing_country: targetCountry,
          billing_state: targetState,
          tax_id: targetTaxId
        },
        currency_symbol: activeSymbol,
        currency_code: targetCurrency,
        order: {
          id: `order_local_${Date.now()}`,
          amount: Math.round((base + taxTotal) * 100),
          currency: targetCurrency
        }
      });
      setStep("ORDER_SUMMARY");
    } finally {
      setOrderLoading(false);
    }
  };

  // 1. User clicks "Upgrade to this Plan" -> Calculate taxes & open Order Summary
  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    const planCurr = plan.currency || (billingCountry.toLowerCase() === "india" ? "INR" : "USD");
    const planSym = plan.currency_symbol || (planCurr === "USD" ? "$" : "₹");
    setCurrencyCode(planCurr);
    setCurrencySymbol(planSym);
    await createOrderWithBcc(plan, billingCountry, billingState, taxId, postalCode, planCurr);
  };

  // 2. User changes billing country
  const handleCountryChange = async (newCountry) => {
    const isIndia = newCountry.toLowerCase() === "india";
    const newState = isIndia ? "Karnataka" : "";
    const newCurr = isIndia ? "INR" : "USD";
    const newSym = isIndia ? "₹" : "$";

    setBillingCountry(newCountry);
    setBillingState(newState);
    setCurrencyCode(newCurr);
    setCurrencySymbol(newSym);

    await createOrderWithBcc(selectedPlan, newCountry, newState, taxId, postalCode, newCurr);
  };

  // 3. User changes billing state -> Recalculate CGST+SGST vs IGST
  const handleStateChange = async (newState) => {
    setBillingState(newState);
    await createOrderWithBcc(selectedPlan, billingCountry, newState, taxId, postalCode, currencyCode);
  };

  // 3. User clicks Proceed to Pay -> Launch native Razorpay checkout modal
  const handleProceedToPay = async () => {
    setError("");
    setOrderLoading(true);

    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      setError("Failed to load Razorpay payment gateway. Please check your network connection.");
      setOrderLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const tenantId = getResolvedTenantId();

      let currentOrder = orderData?.order;
      let breakdown = orderData?.breakdown;

      if (!currentOrder || !currentOrder.id) {
        const orderRes = await axios.post(
          `${API_URL}/api/subscription/create-order`,
          {
            price_id: selectedPlan.price_id || selectedPlan.id,
            billing_country: billingCountry,
            billing_state: billingState,
            postal_code: postalCode,
            tax_id: taxId,
            currency: currencyCode,
            tenant_id: tenantId
          },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        currentOrder = orderRes.data?.data?.order;
        breakdown = orderRes.data?.data?.breakdown;
      }

      const activeCurrency = currentOrder?.currency || currencyCode || (billingCountry.toLowerCase() === "india" ? "INR" : "USD");

      const options = {
        key: currentOrder?.key_id,
        amount: currentOrder?.amount || Math.round((breakdown?.total_amount || 0) * 100),
        currency: activeCurrency,
        name: "Bridgeme Technologies",
        description: `Upgrade to ${breakdown?.plan_name || selectedPlan?.plan_name || "Subscription"}`,
        order_id: currentOrder?.id && !currentOrder.id.startsWith("order_local_") ? currentOrder.id : undefined,
        prefill: {
          name: breakdown?.company_name || localStorage.getItem("company_name") || "",
          email: breakdown?.contact_email || localStorage.getItem("user_email") || ""
        },
        theme: { color: "#4f46e5" },
        handler: async function (response) {
          setStep("PROCESSING");
          try {
            const verifyRes = await axios.post(
              `${API_URL}/api/subscription/upgrade`,
              {
                tenant_id: tenantId,
                product_code: "PULSEHR",
                price_id: selectedPlan.price_id || selectedPlan.id,
                billing_country: billingCountry,
                billing_state: billingState,
                postal_code: postalCode,
                tax_id: taxId,
                currency: activeCurrency,
                razorpay_order_id: response.razorpay_order_id || currentOrder?.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json"
                },
                timeout: 20000
              }
            );

            if (verifyRes.data?.success) {
              const receipt = verifyRes.data.receipt || {
                invoice_number: verifyRes.data.invoice_number || `INV-${Date.now().toString().slice(-6)}`,
                payment_id: response.razorpay_payment_id || "pay_verified",
                amount_paid: breakdown?.total_amount || 0,
                currency_symbol: orderData?.currency_symbol || currencySymbol,
                currency_code: activeCurrency,
                billing_country: billingCountry,
                billing_state: billingState,
                plan_name: breakdown?.plan_name || selectedPlan?.plan_name,
                pdf_url: verifyRes.data.pdf_url || null
              };

              setReceiptData(receipt);
              setStep("SUCCESS");

              const updatedSub = verifyRes.data?.data?.subscription || {
                status: "ACTIVE",
                is_trial: false,
                days_remaining: 365,
                plan_name: selectedPlan.plan_name || selectedPlan.name
              };

              localStorage.setItem("subscription", JSON.stringify(updatedSub));
              window.dispatchEvent(new Event("storage"));
              window.dispatchEvent(new CustomEvent("authChange", { detail: updatedSub }));

              if (onUpgradeSuccess) onUpgradeSuccess(updatedSub);
            } else {
              setError(verifyRes.data?.message || "Payment verification failed.");
              setStep("ORDER_SUMMARY");
            }
          } catch (err) {
            setError(err.response?.data?.message || err.message || "Payment verification failed on server.");
            setStep("ORDER_SUMMARY");
          }
        },
        modal: {
          ondismiss: function () {
            setOrderLoading(false);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not initialize Razorpay payment.");
    } finally {
      setOrderLoading(false);
    }
  };

  // Helper to pick icons per plan index
  const getPlanIcon = (idx) => {
    switch (idx % 5) {
      case 0:
        return {
          icon: <TimerOutlinedIcon sx={{ color: "#d97706", fontSize: "1.2rem" }} />,
          bg: "rgba(245, 158, 11, 0.15)"
        };
      case 1:
        return {
          icon: <GroupOutlinedIcon sx={{ color: "#2563eb", fontSize: "1.2rem" }} />,
          bg: "rgba(37, 99, 235, 0.12)"
        };
      case 2:
        return {
          icon: <StarIcon sx={{ color: "#7c3aed", fontSize: "1.2rem" }} />,
          bg: "rgba(124, 58, 237, 0.12)"
        };
      case 3:
        return {
          icon: <MilitaryTechOutlinedIcon sx={{ color: "#059669", fontSize: "1.2rem" }} />,
          bg: "rgba(5, 150, 105, 0.12)"
        };
      default:
        return {
          icon: <DomainOutlinedIcon sx={{ color: "#dc2626", fontSize: "1.2rem" }} />,
          bg: "rgba(220, 38, 38, 0.12)"
        };
    }
  };

  const b = orderData?.breakdown;

  return (
    <Dialog
      open={isModalOpen}
      onClose={step === "PROCESSING" ? undefined : onClose}
      maxWidth={step === "SELECT_PLAN" ? "xl" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          bgcolor: "#ffffff",
          maxWidth: step === "SELECT_PLAN" ? "1280px" : "560px",
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)"
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          p: "20px 28px 16px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "1px solid #f1f5f9",
          bgcolor: "#fcfcfd"
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: "#4f46e5", fontSize: "1.3rem" }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.25rem", letterSpacing: "-0.3px" }}
            >
              {step === "SELECT_PLAN" && "Upgrade PulseHR Subscription"}
              {step === "ORDER_SUMMARY" && "Order Summary & Tax Split"}
              {step === "PROCESSING" && "Verifying Payment..."}
              {step === "SUCCESS" && "Payment Successful & Invoiced!"}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem", mt: 0.4 }}>
            {step === "SELECT_PLAN" && "Managed & provisioned securely via Bridge Control Center (BCC)"}
            {step === "ORDER_SUMMARY" && "Review transparent GST split and billing state before checkout"}
            {step === "PROCESSING" && "Please wait while we verify your transaction and provision licenses"}
            {step === "SUCCESS" && "Your subscription is now active and a tax invoice has been emailed"}
          </Typography>
        </Box>

        {step !== "PROCESSING" && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: "24px 28px 24px 28px" }}>
        {error && (
          <Alert
            severity="error"
            action={
              step === "SELECT_PLAN" ? (
                <Button color="inherit" size="small" onClick={fetchSubscriptionAndPlans} startIcon={<RefreshIcon />}>
                  Retry
                </Button>
              ) : null
            }
            sx={{ mb: 2.5, borderRadius: "10px" }}
          >
            {error}
          </Alert>
        )}

        {/* ========================================================
            STEP 1: SELECT PLAN
           ======================================================== */}
        {step === "SELECT_PLAN" && (
          <>
            {/* Current Trial / Subscription Banner */}
            <Box
              sx={{
                background: subscriptionInfo.isGrace
                  ? "linear-gradient(135deg, #b45309 0%, #d97706 50%, #f59e0b 100%)"
                  : subscriptionInfo.status === "Expired"
                  ? "linear-gradient(135deg, #991b1b 0%, #dc2626 100%)"
                  : "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                borderRadius: "14px",
                p: "20px 24px",
                color: "#ffffff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
                boxShadow: subscriptionInfo.isGrace
                  ? "0 4px 14px rgba(217, 119, 6, 0.35)"
                  : "none"
              }}
            >
              <Box>
                <Box
                  sx={{
                    bgcolor: subscriptionInfo.isGrace ? "#ffffff" : "#f59e0b",
                    color: subscriptionInfo.isGrace ? "#9a3412" : "#000000",
                    fontSize: "11px",
                    fontWeight: 800,
                    letterSpacing: "0.4px",
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "6px",
                    display: "inline-block",
                    mb: 1,
                    textTransform: "uppercase"
                  }}
                >
                  {subscriptionInfo.isGrace
                    ? "15-DAY GRACE PERIOD ACTIVE"
                    : subscriptionInfo.status === "Expired"
                    ? "SUBSCRIPTION EXPIRED"
                    : subscriptionInfo.isTrial
                    ? "CURRENT TRIAL"
                    : "ACTIVE PLAN"}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: "1.3rem", mb: 0.4 }}>
                  {subscriptionInfo.isGrace
                    ? `${subscriptionInfo.graceDaysRemaining ?? 15} Days Left in Grace Period`
                    : subscriptionInfo.status === "Expired"
                    ? "Subscription Expired — Action Required"
                    : subscriptionInfo.daysRemaining !== null
                    ? `${subscriptionInfo.daysRemaining} Days Left in Free Trial`
                    : "Active PulseHR Subscription"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: subscriptionInfo.isGrace ? "#fff7ed" : "#bfdbfe",
                    fontSize: "0.82rem"
                  }}
                >
                  {subscriptionInfo.isGrace
                    ? "Your billing cycle ended. Complete your renewal now to prevent employee disruption and system lockout."
                    : "Upgrade now to unlock full HRMS automation, AI assistants, and statutory compliance."}
                </Typography>
              </Box>

              <Box
                sx={{
                  bgcolor: "rgba(15, 23, 42, 0.35)",
                  borderRadius: "12px",
                  p: "10px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 3
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#93c5fd", fontWeight: 700, fontSize: "10px", display: "block", textTransform: "uppercase" }}
                  >
                    SEATS USED
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#ffffff", fontSize: "1.1rem" }}>
                    {subscriptionInfo.seatsUsed} {subscriptionInfo.maxSeats ? `/ ${subscriptionInfo.maxSeats}` : ""}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#93c5fd", fontWeight: 700, fontSize: "10px", display: "block", textTransform: "uppercase" }}
                  >
                    STATUS
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: subscriptionInfo.status === "Expired" ? "#ef4444" : "#4ade80",
                      fontSize: "1.1rem"
                    }}
                  >
                    {subscriptionInfo.status}
                  </Typography>
                </Box>

                {subscriptionInfo.storageUsedFormatted && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#93c5fd", fontWeight: 700, fontSize: "10px", display: "block", textTransform: "uppercase" }}
                    >
                      STORAGE
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#ffffff", fontSize: "1.1rem" }}>
                      {subscriptionInfo.storageUsedFormatted}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Targeted alert when triggered by a storage limit exceed or locked feature */}
            {lockedFeature && (
              <Alert
                severity={lockedFeature.includes("STORAGE") ? "error" : "info"}
                sx={{
                  mb: 3,
                  borderRadius: "12px",
                  fontWeight: 500,
                  "& .MuiAlert-message": { fontSize: "0.88rem" }
                }}
              >
                {lockedFeature.includes("STORAGE")
                  ? "Storage Quota Exceeded: Your company has reached its Cloudinary file storage limit. Select a higher plan below to unlock additional storage and resume file uploads."
                  : `Upgrade required: Please upgrade your subscription to access ${lockedFeature}.`}
              </Alert>
            )}

            {/* Plans List Loading / Empty / Cards */}
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
                <CircularProgress size={44} sx={{ color: "#4f46e5" }} />
                <Typography variant="body2" sx={{ color: "#64748b", mt: 2, fontWeight: 500 }}>
                  Loading pricing plans from BCC service...
                </Typography>
              </Box>
            ) : plans.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="body1" sx={{ color: "#64748b", mb: 2 }}>
                  No pricing plans available from BCC service.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchSubscriptionAndPlans}
                  startIcon={<RefreshIcon />}
                  sx={{ borderRadius: "10px", textTransform: "none" }}
                >
                  Refresh Plans
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: plans.length >= 4 ? "repeat(4, 1fr)" : `repeat(${plans.length}, 1fr)`,
                    lg: plans.length >= 5 ? "repeat(5, 1fr)" : `repeat(${plans.length}, 1fr)`
                  },
                  gap: 2,
                  alignItems: "stretch"
                }}
              >
                {plans.map((plan, idx) => {
                  const isPopular = Boolean(plan.is_popular || plan.popular);
                  const isSelecting = orderLoading && selectedPlan?.price_id === plan.price_id;
                  const iconObj = getPlanIcon(idx);

                  const planName = plan.plan_name || plan.name || `Plan #${plan.price_id}`;
                  const planCurr = plan.currency || currencyCode || (billingCountry.toLowerCase() === "india" ? "INR" : "USD");
                  const currencySymbol = plan.currency_symbol || (planCurr === "USD" ? "$" : (currencySymbol || "₹"));
                  const localeToUse = planCurr === "INR" ? "en-IN" : "en-US";

                  const formattedPrice =
                    plan.formatted_price ||
                    (plan.amount !== undefined && plan.amount !== null
                      ? `${currencySymbol}${Number(plan.amount).toLocaleString(localeToUse)}`
                      : "Custom");

                  const periodDisplay = plan.billing_frequency
                    ? `/ ${plan.billing_frequency.toLowerCase()}`
                    : (plan.period || "");

                  const usersChipText =
                    plan.max_users !== undefined && plan.max_users !== null
                      ? `${plan.max_users} Users Max`
                      : (plan.users_limit ? `${plan.users_limit} Users` : "Seats Included");

                  const hasActiveDiscount = Boolean(
                    plan.is_discount_active &&
                    (
                      (plan.savings_amount !== undefined && Number(plan.savings_amount) > 0) ||
                      (plan.final_amount !== undefined && plan.amount !== undefined && Number(plan.final_amount) < Number(plan.amount))
                    )
                  );

                  const originalPrice =
                    plan.formatted_price ||
                    (plan.amount !== undefined && plan.amount !== null
                      ? `${currencySymbol}${Number(plan.amount).toLocaleString(localeToUse)}`
                      : null);

                  const finalPrice =
                    plan.formatted_final_price ||
                    (plan.final_amount !== undefined && plan.final_amount !== null
                      ? `${currencySymbol}${Number(plan.final_amount).toLocaleString(localeToUse)}`
                      : formattedPrice);

                  const formattedSavings =
                    plan.formatted_savings ||
                    (plan.savings_amount !== undefined && plan.savings_amount !== null && Number(plan.savings_amount) > 0
                      ? `${currencySymbol}${Number(plan.savings_amount).toLocaleString(localeToUse)}`
                      : null);

                  const campaignTitle = plan.discount_campaign_name || "Special Offer";
                  const discountPct = plan.discount_percentage ? `${plan.discount_percentage}% OFF` : "";
                  const urgency =
                    plan.discount_urgency_text ||
                    (plan.discount_days_left !== undefined && plan.discount_days_left !== null
                      ? `Only ${plan.discount_days_left} day${plan.discount_days_left === 1 ? "" : "s"} left!`
                      : "");

                  // Parse pointwise features
                  let rawFeatures = [];
                  if (Array.isArray(plan.features)) {
                    rawFeatures = plan.features.flatMap((f) =>
                      typeof f === "string" ? f.split(/[,;\n]/) : f
                    );
                  } else if (typeof plan.features === "string" && plan.features.trim()) {
                    rawFeatures = plan.features.split(/[,;\n]/);
                  }
                  if (typeof plan.slab_name === "string" && plan.slab_name.trim()) {
                    rawFeatures = [...rawFeatures, ...plan.slab_name.split(/[,;\n]/)];
                  }
                  if (typeof plan.slab_description === "string" && plan.slab_description.trim()) {
                    rawFeatures = [...rawFeatures, ...plan.slab_description.split(/[,;\n]/)];
                  }

                  const featureList = Array.from(
                    new Set(
                      rawFeatures
                        .map((item) => (typeof item === "string" ? item.trim() : String(item).trim()))
                        .filter((item) => item.length > 0)
                    )
                  );

                  return (
                    <Box
                      key={plan.price_id || idx}
                      sx={{
                        border: isPopular ? "2px solid #7c3aed" : "1px solid #e2e8f0",
                        borderRadius: "14px",
                        p: "20px 18px",
                        bgcolor: "#ffffff",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 10px 25px rgba(0,0,0,0.07)",
                          borderColor: isPopular ? "#7c3aed" : "#cbd5e1"
                        }
                      }}
                    >
                     
                      <Box>
                        {/* Card Header */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <Box
                            sx={{
                              bgcolor: iconObj.bg,
                              p: 0.6,
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {iconObj.icon}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>
                            {planName}
                          </Typography>
                        </Box>

                        {/* Festive / Promotional Banner */}
                        {hasActiveDiscount && (
                          <Box
                            sx={{
                              background: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
                              border: "1px solid #fecdd3",
                              borderRadius: "8px",
                              p: "6px 10px",
                              mb: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8
                            }}
                          >
                            <span style={{ fontSize: "13px" }}>🏷️</span>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#be123c",
                                fontWeight: 700,
                                fontSize: "11px",
                                lineHeight: 1.3
                              }}
                            >
                              <strong>{campaignTitle}</strong>
                              {discountPct ? `: ${discountPct}` : ""}
                              {urgency ? (
                                <span style={{ color: "#9f1239", fontWeight: 800 }}> — {urgency}</span>
                              ) : null}
                            </Typography>
                          </Box>
                        )}

                        {/* Price Display */}
                        <Box sx={{ mb: 1.2 }}>
                          {hasActiveDiscount ? (
                            <Box>
                              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, flexWrap: "wrap" }}>
                                {originalPrice && (
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{
                                      color: "#94a3b8",
                                      textDecoration: "line-through",
                                      fontWeight: 600,
                                      fontSize: "0.95rem"
                                    }}
                                  >
                                    {originalPrice}
                                  </Typography>
                                )}
                                <Typography
                                  variant="h5"
                                  component="span"
                                  sx={{ fontWeight: 900, color: "#0f172a", fontSize: "1.45rem" }}
                                >
                                  {finalPrice}
                                </Typography>
                                {periodDisplay && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.8rem" }}>
                                    {periodDisplay}
                                  </Typography>
                                )}
                              </Box>
                              {formattedSavings && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#16a34a",
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    display: "inline-block",
                                    mt: 0.2
                                  }}
                                >
                                  (Save {formattedSavings})
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
                              <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a", fontSize: "1.45rem" }}>
                                {finalPrice || formattedPrice}
                              </Typography>
                              {periodDisplay && (
                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.8rem" }}>
                                  {periodDisplay}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* AI Tokens Included Badge */}
                        {plan.ai_tokens !== undefined && plan.ai_tokens !== null && Number(plan.ai_tokens) > 0 && (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.6,
                              bgcolor: "rgba(245, 158, 11, 0.12)",
                              color: "#b45309",
                              border: "1px solid rgba(245, 158, 11, 0.25)",
                              borderRadius: "6px",
                              px: 1,
                              py: 0.35,
                              fontSize: "11px",
                              fontWeight: 700,
                              mb: 1.2,
                              width: "fit-content"
                            }}
                          >
                            <span style={{ fontSize: "12px" }}>⚡</span>
                            <span>{Number(plan.ai_tokens).toLocaleString()} AI Tokens Included</span>
                          </Box>
                        )}

                        {/* User Seats & Slab Chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1.5 }}>
                          <Box
                            sx={{
                              bgcolor: "#f1f5f9",
                              color: "#475569",
                              fontWeight: 700,
                              fontSize: "11px",
                              px: 1,
                              py: 0.3,
                              borderRadius: "6px",
                              display: "inline-block"
                            }}
                          >
                            {usersChipText}
                          </Box>

                          {plan.slab_name && !plan.slab_name.includes(",") && (
                            <Box
                              sx={{
                                bgcolor: "rgba(124, 58, 237, 0.08)",
                                color: "#6d28d9",
                                fontWeight: 700,
                                fontSize: "11px",
                                px: 1,
                                py: 0.3,
                                borderRadius: "6px",
                                display: "inline-block"
                              }}
                            >
                              {plan.slab_name}
                            </Box>
                          )}
                        </Box>

                        {/* Description */}
                        {plan.description && !plan.description.includes(",") && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#64748b", fontSize: "0.78rem", minHeight: "30px", lineHeight: 1.35, mb: 1.5 }}
                          >
                            {plan.description}
                          </Typography>
                        )}

                        {/* Pointwise Features List */}
                        {featureList.length > 0 && (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {featureList.map((feature, fIdx) => (
                              <Box key={fIdx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}>
                                <Box
                                  sx={{
                                    color: "#16a34a",
                                    bgcolor: "rgba(22, 163, 74, 0.12)",
                                    borderRadius: "50%",
                                    p: "2px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mt: "2px",
                                    flexShrink: 0
                                  }}
                                >
                                  <CheckIcon sx={{ fontSize: "11px", fontWeight: "bold" }} />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#334155", fontSize: "0.78rem", fontWeight: 500, lineHeight: 1.3 }}
                                >
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* Upgrade Plan Button */}
                      <Box sx={{ mt: 2.5 }}>
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => handleSelectPlan(plan)}
                          disabled={isSelecting || orderLoading}
                          endIcon={!isSelecting ? <ArrowForwardIcon sx={{ fontSize: "14px" }} /> : null}
                          sx={{
                            bgcolor: isPopular ? "#7c3aed" : "#0f172a",
                            color: "#ffffff",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            textTransform: "none",
                            borderRadius: "8px",
                            py: 0.9,
                            "&:hover": {
                              bgcolor: isPopular ? "#6d28d9" : "#1e293b"
                            }
                          }}
                        >
                          {isSelecting ? (
                            <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                          ) : (
                            `Upgrade to ${planName}`
                          )}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {/* ========================================================
            STEP 2: ORDER SUMMARY & GST BREAKDOWN
           ======================================================== */}
        {step === "ORDER_SUMMARY" && b && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Selected Plan Summary Banner */}
            <Box
              sx={{
                p: "14px 18px",
                bgcolor: "#eef2ff",
                borderRadius: "12px",
                border: "1px solid #c7d2fe",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e1b4b" }}>
                  {b.plan_name}
                </Typography>
                <Typography variant="caption" sx={{ color: "#4338ca", fontWeight: 500 }}>
                  {b.billing_frequency} Subscription • Up to {b.max_users || 20} User Seats Included
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setStep("SELECT_PLAN")}
                sx={{ textTransform: "none", color: "#4f46e5", fontWeight: 700, fontSize: "0.8rem" }}
              >
                Change Plan
              </Button>
            </Box>

            {/* Billing Destination & Tax Details */}
            {(() => {
              const currSymbol = orderData?.currency_symbol || selectedPlan?.currency_symbol || currencySymbol || "₹";
              const isIndia = billingCountry.toLowerCase() === "india";
              const activeLocale = (currencyCode || (isIndia ? "INR" : "USD")).toUpperCase() === "INR" ? "en-IN" : "en-US";

              return (
                <>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    {/* Country Selector */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 0.6, textTransform: "uppercase", letterSpacing: "0.4px" }}
                      >
                        Billing Country
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={billingCountry}
                          onChange={(e) => handleCountryChange(e.target.value)}
                          disabled={orderLoading}
                          sx={{
                            borderRadius: "10px",
                            bgcolor: "#ffffff",
                            fontSize: "0.88rem",
                            fontWeight: 600
                          }}
                        >
                          {SUPPORTED_COUNTRIES.map((ctry) => (
                            <MenuItem key={ctry} value={ctry}>
                              {ctry}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* State Selector / Input */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 0.6, textTransform: "uppercase", letterSpacing: "0.4px" }}
                      >
                        {isIndia ? "State (Determines CGST/SGST vs IGST)" : "State / Province / Region"}
                      </Typography>
                      {isIndia ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={billingState}
                            onChange={(e) => handleStateChange(e.target.value)}
                            disabled={orderLoading}
                            sx={{
                              borderRadius: "10px",
                              bgcolor: "#ffffff",
                              fontSize: "0.88rem",
                              fontWeight: 600
                            }}
                          >
                            {INDIAN_STATES.map((st) => (
                              <MenuItem key={st} value={st}>
                                {st} {st === "Karnataka" ? " (Intra-State: CGST 9% + SGST 9%)" : " (Inter-State: IGST 18%)"}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g. California, Dubai, London"
                          value={billingState}
                          onChange={(e) => setBillingState(e.target.value)}
                          onBlur={(e) => handleStateChange(e.target.value)}
                          disabled={orderLoading}
                          sx={{
                            bgcolor: "#ffffff",
                            "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.88rem", fontWeight: 600 }
                          }}
                        />
                      )}
                    </Box>

                    {/* Postal / ZIP Code */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 0.6, textTransform: "uppercase", letterSpacing: "0.4px" }}
                      >
                        Postal / ZIP Code (Optional)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. 560001 or 90210"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        disabled={orderLoading}
                        sx={{
                          bgcolor: "#ffffff",
                          "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.88rem" }
                        }}
                      />
                    </Box>

                    {/* Tax ID (GSTIN / VAT ID) */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 0.6, textTransform: "uppercase", letterSpacing: "0.4px" }}
                      >
                        {isIndia ? "GSTIN for Tax Invoice (Optional)" : "Tax ID / VAT No (Optional)"}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={isIndia ? "e.g. 29ABCDE1234F1Z5" : "e.g. VAT / Tax Reg No"}
                        value={taxId}
                        onChange={(e) => setTaxId(e.target.value)}
                        disabled={orderLoading}
                        sx={{
                          bgcolor: "#ffffff",
                          "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.88rem" }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Price & Tax Breakdown Card */}
                  <Box
                    sx={{
                      bgcolor: "#f8fafc",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      p: "16px 20px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.2
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem" }}>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>Plan Base Price:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currSymbol}{Number(b.base_price || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>

                    {Number(b.discount_amount) > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem" }}>
                        <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 600 }}>Discount Applied:</Typography>
                        <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 700 }}>
                          - {currSymbol}{Number(b.discount_amount).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem" }}>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>Net Subtotal:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currSymbol}{Number(b.subtotal || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 0.5 }} />

                    {/* Dynamic Tax Lines returned by BCC */}
                    {Array.isArray(b.tax_lines) && b.tax_lines.length > 0 ? (
                      b.tax_lines.map((tLine, tIdx) => (
                        <Box key={tIdx} sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {tLine.label} {tLine.rate !== undefined && tLine.rate !== null ? `(${tLine.rate}%)` : ""}:
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155" }}>
                            {currSymbol}{Number(tLine.amount || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      ))
                    ) : b.tax_type === "CGST_SGST" ? (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>CGST (9%):</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155" }}>
                            {currSymbol}{Number(b.cgst || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
                          <Typography variant="caption" sx={{ color: "#64748b" }}>SGST (9%):</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155" }}>
                            {currSymbol}{Number(b.sgst || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem" }}>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {isIndia ? "IGST (18%)" : "Tax (0% Export / LUT)"}:
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155" }}>
                          {currSymbol}{Number(b.tax_total ?? b.igst ?? 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 0.5 }} />

                    {/* Total Payable */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", pt: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        Total Amount Payable:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: "#4f46e5" }}>
                        {currSymbol}{Number(b.total_amount || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1.5, pt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setStep("SELECT_PLAN")}
                      startIcon={<ArrowBackIcon />}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        color: "#64748b",
                        borderColor: "#cbd5e1",
                        "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" }
                      }}
                    >
                      Back
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleProceedToPay}
                      disabled={orderLoading}
                      startIcon={!orderLoading ? <CreditCardIcon /> : null}
                      sx={{
                        borderRadius: "10px",
                        py: 1.2,
                        bgcolor: "#2563eb",
                        fontWeight: 800,
                        fontSize: "0.92rem",
                        textTransform: "none",
                        boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
                        "&:hover": { bgcolor: "#1d4ed8" }
                      }}
                    >
                      {orderLoading ? (
                        <CircularProgress size={20} sx={{ color: "#ffffff" }} />
                      ) : (
                        `Proceed to Pay ${currSymbol}${Number(b.total_amount || 0).toLocaleString(activeLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      )}
                    </Button>
                  </Box>
                </>
              );
            })()}
          </Box>
        )}

        {/* ========================================================
            STEP 3: PROCESSING SPINNER
           ======================================================== */}
        {step === "PROCESSING" && (
          <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <CircularProgress size={54} sx={{ color: "#4f46e5", mb: 2.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.8 }}>
              Verifying Payment with Gateway...
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 380 }}>
              Please do not close this modal or refresh your browser. We are validating the payment signature, activating your licenses, and generating your tax invoice.
            </Typography>
          </Box>
        )}

        {/* ========================================================
            STEP 4: SUCCESS & TAX INVOICE RECEIPT
           ======================================================== */}
        {step === "SUCCESS" && receiptData && (
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Box
              sx={{
                bgcolor: "rgba(22, 163, 74, 0.12)",
                color: "#16a34a",
                p: 1.5,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 44 }} />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a" }}>
              Subscription Activated Successfully!
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, mb: 2.5, maxWidth: 420 }}>
              Your company has been upgraded to <strong>{receiptData.plan_name}</strong>. An official GST tax invoice has been dispatched to your company contact email.
            </Typography>

            {/* Receipt Summary Card */}
            <Box
              sx={{
                width: "100%",
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                p: "16px 20px",
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                gap: 1.2,
                mb: 3
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>Invoice Number:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {receiptData.invoice_number}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>Payment Reference:</Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#334155" }}>
                  {receiptData.payment_id}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>Amount Paid (incl. Taxes):</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#16a34a" }}>
                  {(receiptData.currency_symbol || currencySymbol)}{Number(receiptData.amount_paid || 0).toLocaleString((receiptData.currency_code || currencyCode) === "INR" ? "en-IN" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>Billing Destination:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#475569" }}>
                  {receiptData.billing_state ? `${receiptData.billing_state}, ` : ""}{receiptData.billing_country || billingCountry}
                </Typography>
              </Box>
            </Box>

            {/* Success Actions */}
            <Box sx={{ display: "flex", gap: 1.5, width: "100%", justifyContent: "center" }}>
              {receiptData.pdf_url && (
                <Button
                  variant="outlined"
                  href={receiptData.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    color: "#0f172a",
                    borderColor: "#cbd5e1"
                  }}
                >
                  Download Invoice PDF
                </Button>
              )}

              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  borderRadius: "10px",
                  bgcolor: "#4f46e5",
                  fontWeight: 800,
                  textTransform: "none",
                  px: 3.5,
                  "&:hover": { bgcolor: "#4338ca" }
                }}
              >
                Done & Return to Dashboard
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpgradePlanModal;
